import { api } from "./api";
import { executarRequisicaoAutenticada } from "./authenticatedRequest";

export type TipoComunicacaoCliente =
  | "CartaMensal"
  | "CotaManutencao"
  | "SistemaAtualizacao"
  | "InformeImpostoRenda";

export interface ComunicacaoCliente {
  id: number;
  tipo: TipoComunicacaoCliente;
  titulo: string;
  mensagem: string;
  urlArquivo?: string;
  nomeArquivo?: string;
  mesReferencia?: string;
  ativo: boolean;
  criadoEm: string;
  atualizadoEm: string;
  lida: boolean;
}

export interface FeedComunicacoesCliente {
  comunicacoes: ComunicacaoCliente[];
  totalNaoLidas: number;
}

export interface GestaoComunicacoesCliente {
  avisos: ComunicacaoCliente[];
  cartasMensais: ComunicacaoCliente[];
}

export interface ComunicacaoMutationResult {
  comunicacao?: ComunicacaoCliente;
  emailsEnviados: number;
  notificacaoFalhou?: boolean;
}

export interface PublicarCartaMensalPayload {
  titulo: string;
  descricao: string;
  mesReferencia: string;
  nomeArquivo: string;
  arquivoBase64: string;
  publicar: boolean;
}

export interface AtualizarCartaMensalPayload {
  titulo: string;
  descricao: string;
  mesReferencia: string;
}

export interface SubstituirArquivoCartaMensalPayload {
  nomeArquivo: string;
  arquivoBase64: string;
}

export interface AtualizarDisponibilidadeInformeImpostoRendaPayload {
  anoCalendario: number;
  titulo: string;
  mensagem: string;
  ativo: boolean;
}

const parseResponse = (data: any) => {
  if (typeof data !== "string") {
    return data;
  }

  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

const getResultado = <T,>(response: any): T => {
  const data = parseResponse(response?.data);
  return data?.resultado ?? data?.camposAdicionais ?? data;
};

const withAuthorization = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

export const obterFeedComunicacoesCliente = async (limite = 12) => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.get("/ComunicacaoCliente/Feed", {
      ...withAuthorization(token),
      params: { limite },
    })
  );

  return getResultado<FeedComunicacoesCliente>(response);
};

export const obterRelatoriosCliente = async () => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.get("/ComunicacaoCliente/Relatorios", withAuthorization(token))
  );

  return (
    getResultado<{ cartasMensais: ComunicacaoCliente[] }>(response)
      ?.cartasMensais || []
  );
};

export const marcarComunicacaoComoLida = async (idComunicacao: number) =>
  executarRequisicaoAutenticada((token) =>
    api.post(
      `/ComunicacaoCliente/${idComunicacao}/Leitura`,
      {},
      withAuthorization(token)
    )
  );

export const obterGestaoComunicacoes = async () => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.get("/ComunicacaoCliente/Gestao", withAuthorization(token))
  );

  return getResultado<GestaoComunicacoesCliente>(response);
};

export const atualizarAvisoPlataforma = async (
  aviso: Pick<ComunicacaoCliente, "tipo" | "titulo" | "mensagem" | "ativo">
) => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.put(
      `/ComunicacaoCliente/Avisos/${aviso.tipo}`,
      aviso,
      withAuthorization(token)
    )
  );

  return getResultado<ComunicacaoMutationResult>(response);
};

export const atualizarDisponibilidadeInformeImpostoRenda = async (
  payload: AtualizarDisponibilidadeInformeImpostoRendaPayload
) => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.put(
      "/ComunicacaoCliente/InformeImpostoRenda",
      payload,
      withAuthorization(token)
    )
  );

  return getResultado<ComunicacaoMutationResult>(response);
};

export const publicarCartaMensal = async (payload: PublicarCartaMensalPayload) => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.post(
      "/ComunicacaoCliente/CartaMensal",
      payload,
      withAuthorization(token)
    )
  );

  return getResultado<ComunicacaoMutationResult>(response);
};

export const alterarStatusCartaMensal = async (id: number, ativo: boolean) => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.put(
      `/ComunicacaoCliente/CartaMensal/${id}/Status`,
      { id, ativo },
      withAuthorization(token)
    )
  );

  return getResultado<ComunicacaoMutationResult>(response);
};

export const atualizarCartaMensal = async (
  id: number,
  payload: AtualizarCartaMensalPayload
) => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.put(
      `/ComunicacaoCliente/CartaMensal/${id}`,
      payload,
      withAuthorization(token)
    )
  );

  return getResultado<ComunicacaoMutationResult>(response);
};

export const substituirArquivoCartaMensal = async (
  id: number,
  payload: SubstituirArquivoCartaMensalPayload
) => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.put(
      `/ComunicacaoCliente/CartaMensal/${id}/Arquivo`,
      payload,
      withAuthorization(token)
    )
  );

  return getResultado<ComunicacaoMutationResult>(response);
};

export const excluirCartaMensal = async (id: number) => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.delete(`/ComunicacaoCliente/CartaMensal/${id}`, withAuthorization(token))
  );

  return getResultado(response);
};
